import React, { useContext, useEffect } from 'react'

import { AxiosError } from 'axios'
import moment from 'moment'

import { Card, Flex, Header, Icon, Link, Table, Text, useTableState } from '@driftt/dds'
import { faUpRightFromSquare } from '@driftt/dds.icons/regular/faUpRightFromSquare'

import { driftLoadingStateBuilder } from 'components/States'
import { CurrentUserContext } from 'context/currentUserContext'
import { Opportunity } from 'modules/accountActivity/domain'
import { formatDollars } from 'utils/formatNumbers'
import { captureExceptionWithMessage } from 'utils/logRocket'
import { segmentTrack, TrackType } from 'utils/tracking'

export interface OpportunitiesTabProps {
  accountId: string
  salesforceData: any | undefined
  opportunities: any | undefined
  opportunitiesError: AxiosError | undefined | null
}

const OpportunitiesTab = ({ accountId, salesforceData, opportunities, opportunitiesError }: OpportunitiesTabProps) => {

  const currentUser = useContext(CurrentUserContext)
  useEffect(() => {
    segmentTrack(TrackType.SFDC_OPEN_OPPORTUNITIES_TAB_VIEWED, {
      userId: currentUser?.id,
      orgId: currentUser?.orgId
    })
    // Run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const salesforceAccount = salesforceData?.accounts ? salesforceData?.accounts[0] : {}

  const { tableState, handleTableStateChange } = useTableState<any>({
    defaultPagination: {
      pageIndex: 0,
      pageSize: 5
    }
  })

  const LoadingComponent = React.useMemo(
    () => driftLoadingStateBuilder('Loading your opportunities...'),
    []
  )
  const isLoading = !opportunities && !opportunitiesError

  if (opportunitiesError) {
    captureExceptionWithMessage('Error fetching opportunities', opportunitiesError, { accountId })
  }

  if (isLoading) {
    return <LoadingComponent />
  }

  return (
    <Flex fullWidth>
      <Card
        css={{
          width: '100%',
          padding: '$2'
        }}>
        <Card.Header>
          <Flex fullWidth justifyContent="space-between" alignItems="center">
            <Header variant="h3">Opportunities</Header>
            {salesforceData?.baseUrl && salesforceAccount?.Id && (
              <Link
                external
                variant="inline"
                href={`${salesforceData.baseUrl}lightning/r/Account/${salesforceAccount.Id}/view`}>
                <Flex alignItems="center">
                  <Icon icon={faUpRightFromSquare} />
                  View in Salesforce
                </Flex>
              </Link>
            )}
          </Flex>
        </Card.Header>
        <Card.Content
          css={{
            margin: '0 -$2'
          }}>
          <Table<Opportunity>
            disableBoxShadow
            emptyState={{
              title: 'No opportunities to display',
              subtitle: ''
            }}
            columns={
              [
                {
                  value: 'opportunityName',
                  header: 'Opportunity name',
                  size: 170
                },
                {
                  value: 'ownerName',
                  header: 'Owner',
                  size: 120,
                  cell: ({ownerName}) => {
                    return <Text css={{maxWidth: '120px'}} truncate>{ownerName}</Text>
                  }
                },
                {
                  value: 'opportunityCloseDate',
                  header: 'Close date',
                  size: 100,
                  cell: ({ opportunityCloseDate}) =>
                    moment(opportunityCloseDate).format('MM/DD/YYYY')
                },
                {
                  value: 'opportunityStageName',
                  header: 'Stage',
                },
                {
                  value: 'opportunityAmount',
                  header: 'Amount',
                  size: 70,
                  cell: ({opportunityAmount}) =>
                    opportunityAmount ? formatDollars(parseInt(opportunityAmount)) : '-'
                },
                {
                  value: 'opportunityType',
                  header: 'Type'
                }
              ]
            }
            rows={opportunities as Opportunity[] ?? ([] as Opportunity[])}
            onStateChange={handleTableStateChange}
            pagination={{
              rowTotal: opportunities?.length ?? 0,
              defaultPagination: tableState.pagination,
              enableClientSidePagination: true
            }}
            sorting={{
              defaultSort: [{ id: 'opportunityCloseDate', desc: true }]
            }}
          />
        </Card.Content>
      </Card>
    </Flex>
  )
}

export default OpportunitiesTab
